<script lang="ts">
    import { getContainer } from '$components/Context/ContainerContext.js';
    import FormErrors from '$components/Form/FormErrors.svelte';
    import HtmlForm from '$components/Form/HtmlForm.svelte';
    import Row from '$components/Form/Row.svelte';
    import Textarea from '$components/Form/Textarea.svelte';
    import { createForm, createOnSubmit } from './MultiQuoteFormData';
    import { cx } from '$lib/utils/cx';
    import SelectCountry from '$components/Form/SelectCountry/SelectCountry.svelte';
    import SelectCityId from '$components/Form/SelectCity/SelectCityId.svelte';
    import Input from '$components/Form/Input.svelte';
    import * as t from '$trans';
    import BBCode from '$components/BBCode/BBCode.svelte';
    import SelectCategoryInHierarchy from '$components/Form/SelectCategoryInHierarchy/SelectCategoryInHierarchy.svelte';
    import CarRentalForm from '$components/MultiQuoteForm/CarRental/CarRentalForm.svelte';
    import AssuranceForm from '$components/MultiQuoteForm/Assurance/AssuranceForm.svelte';
    import TransitForm from '$components/MultiQuoteForm/Transit/TransitForm.svelte';
    import { fly } from 'svelte/transition';
    import { quadOut } from 'svelte/easing';

    let initialCategoryId: number | null = null;
    export { initialCategoryId as categoryId };

    export let withCategory: boolean;

    export let relatedCmsPostId: number | null = null;
    let initialCountry: string | null = null;
    export { initialCountry as country };

    export let htmlClass = '';
    export let successClass = 'alert alert-success';

    const { currentUserStore, lang } = getContainer();

    const currentUserId = currentUserStore().currentUserId();

    let [form, fields] = createForm('multi-quote-form', {
        user: $currentUserId,
        categoryId: initialCategoryId,
        relatedCmsPostId,
        country: initialCountry?.toUpperCase() ?? null,
        message: ``,
    });

    $: country = form.stores().fieldValue(fields.country);
    $: categoryId = form.stores().fieldValue(fields.categoryId);

    $: onSubmit = createOnSubmit(getContainer());

    let successMessage: string | null = null;
</script>

{#if successMessage != null}
    <div class={successClass}>
        <BBCode bbcode={successMessage} />
    </div>
{:else}
    <HtmlForm
        class={cx(htmlClass)}
        {form}
        {onSubmit}
        onSubmitSuccess={({ form, data }) => {
            form.dispatch((_type) => form.getInitialState());
            if (data.userJustCreated === true) {
                successMessage = t.multiquote.alert__created_with_registration({}, $lang);
            } else {
                successMessage = t.multiquote.alert__created({}, $lang);
            }
        }}
    >
        <div class="flex flex-col gap-8">
            {#if withCategory}
                <Row
                    {form}
                    field={fields.categoryId}
                    class=""
                    let:field
                    let:id
                    label={`Secteur d'activité`}
                >
                    <SelectCategoryInHierarchy
                        {form}
                        {field}
                        validation="force-leaf"
                        parentId={initialCategoryId ?? null}
                        {id}
                    />
                </Row>
            {/if}

            {#if $currentUserId == null}
                <div class="grid grid-cols-1 t:grid-cols-2 gap-8">
                    <Row
                        class=""
                        {form}
                        field={fields.firstname}
                        label={'Prénom'}
                        required
                        let:field
                    >
                        <Input
                            {form}
                            {field}
                            toText={(id) => id}
                            fromText={(id) => id}
                            class={''}
                        />
                    </Row>

                    <Row
                        class=""
                        {form}
                        field={fields.lastname}
                        label={'Nom'}
                        required
                        let:field
                    >
                        <Input
                            {form}
                            {field}
                            toText={(id) => id}
                            fromText={(id) => id}
                            class={''}
                        />
                    </Row>
                </div>

                <Row
                    class=""
                    {form}
                    field={fields.email}
                    label={'Email'}
                    required
                    let:field
                >
                    <Input
                        {form}
                        {field}
                        type="email"
                        toText={(id) => id}
                        fromText={(id) => id}
                        class={''}
                    />
                </Row>
            {/if}

            <Row class="" {form} field={fields.phone} label={'Telephone'} let:field>
                <Input
                    {form}
                    {field}
                    toText={(id) => id}
                    fromText={(id) => id}
                    class={''}
                />
            </Row>

            <div class={'grid grid-cols-1 t:grid-cols-2 gap-8'}>
                <Row {form} class="" field={fields.country} label="Pays" let:field>
                    <SelectCountry clearable {form} {field} placeholder="Pays" />
                </Row>

                {#if $country != null}
                    <div
                        in:fly={{
                            duration: 150,
                            easing: quadOut,
                            x: '-10px',
                            opacity: 0,
                        }}
                    >
                        <Row {form} class="" field={fields.city} label="Ville" let:field>
                            <SelectCityId {form} {field} country={$country} />
                        </Row>
                    </div>
                {/if}
            </div>

            {#if $categoryId === 411}
                <CarRentalForm {form} fields={fields.carRental} />
            {:else if $categoryId === 173 || $categoryId === 174}
                <AssuranceForm {form} fields={fields.assurance} />
            {:else if $categoryId === 323}
                <TransitForm {form} fields={fields.transit} />
            {/if}

            <Row
                class=""
                {form}
                field={fields.message}
                label={'Votre demande'}
                required
                let:field
            >
                <p class="">
                    Soyez le plus précis dans votre demande de devis : biens ou services
                    recherchés, les quantités, les délais de livraison…
                </p>

                <Textarea
                    {form}
                    {field}
                    placeholder={`Soyez le plus précis dans votre demande de devis : biens ou services recherchés, les quantités, les délais de livraison…`}
                    toText={(id) => id}
                    fromText={(id) => id}
                    class={''}
                    rows={5}
                />
            </Row>
        </div>

        <div class="flex flex-col gap-8">
            <FormErrors {form} displayIfHasFieldsErrors />
        </div>

        <slot {form} />
    </HtmlForm>
{/if}
